import React, { memo, useEffect } from 'react';
import { initTrackingScripts } from '../lib/tracking-scripts';
import { createComponent, getComponentConfiguration } from '../lib/component';
import {
  EventParamTypes,
  eventParamAttributes,
} from '@spotify-internal/touchdown-analytics';

import '@spotify-internal/landing-page-library/esm/styles/masthead/index.css';

export type ComponentData = {
  name: string;
  attributes?: object;
  eventParams?: EventParamTypes;
};

export type PageMetadata = {
  title?: string;
  description?: string;
  image?: string;
  video?: string;
};

export type PageData = {
  metadata?: PageMetadata;
  components: ComponentData[];
  eventParams?: {
    offerTypeId?: string;
    campaignId?: string;
  };
  themeName?: string;
};

export type UserContextType = {
  ineligibility_flag: string | null;
  authenticated: boolean;
};

const Page = ({
  forceOneTrust,
  market,
  language,
  direction,
  user,
  pageData,
  embedded = false,
}: {
  forceOneTrust?: boolean;
  market: string;
  language: string;
  direction: string;
  user: UserContextType;
  mastheadData?: object;
  pageData: PageData;
  embedded?: boolean;
}) => {
  useEffect(() => {
    initTrackingScripts({
      forceOneTrust,
      market,
      language,
      loggedIn: user.authenticated,
    });
  }, [forceOneTrust, market, language, user.authenticated]);

  const ComponentConfiguration = getComponentConfiguration();

  const { themeName } = pageData;

  const header =
    pageData?.components[0]?.name === 'pageheader'
      ? pageData.components.shift()
      : null;
  const footer =
    pageData?.components[pageData?.components?.length - 1]?.name ===
    'pagefooter'
      ? pageData.components.pop()
      : null;

  const pageEventParams = eventParamAttributes(pageData.eventParams);

  return (
    <ComponentConfiguration
      market={market}
      direction={direction}
      user={user}
      embedded={embedded}
      themeName={themeName}
    >
      {header &&
        createComponent(
          header.name,
          { ...header.attributes },
          header.eventParams,
        )}
      <main {...pageEventParams}>
        {pageData.components.map(({ name, attributes, eventParams }, index) =>
          createComponent(name, { key: index, ...attributes }, eventParams),
        )}
      </main>
      {footer &&
        createComponent(
          footer.name,
          { ...footer.attributes },
          footer.eventParams,
        )}
    </ComponentConfiguration>
  );
};

export default memo(Page);
